import * as React from 'react';
import ValidationMessages from './ValidationMessages';
import { FieldRenderProps } from 'react-final-form';
import { InputProps } from '.';
import { FormGroup, Input as ReactStrapInput } from 'reactstrap';
import FieldLabel from './FieldLabel';
import { toKebabCase } from 'util/StringUtil';
import FieldSubtext from 'components/shared/FieldSubtext';
import classnames from 'classnames';
import { WithTestIdOptional } from 'shared-client/types/test';

export type FieldInputProps = FieldRenderProps<any, HTMLInputElement> & InputProps & WithTestIdOptional;

/**
 * Component that renders a Reactstrap input with form group.
 *
 * @class Input
 * @extends {(React.Component<FieldRenderProps & InputProps>)}
 */
function Input(props: FieldInputProps) {
	const {
		input: { name, onChange, value, onBlur, onFocus },
		meta,
		className,
		type,
		disabled,
		autoFocus,
		readOnly,
		indicateDirty,
		step,
		min,
		max,
		prefix,
		suffix,
		translatedFieldValue,
		translatedFieldName,
		rows,
		cols,
		subtext,
		testId,
		renderIcon,
	} = props;

	const isTranslatedField = !!translatedFieldName;
	const showTranslationPlaceholder = isTranslatedField && !value;

	const inputComponent = (
		<ReactStrapInput
			id={name}
			name={name}
			type={type}
			value={value}
			onChange={onChange}
			onBlur={onBlur}
			onFocus={onFocus}
			disabled={disabled}
			autoFocus={autoFocus}
			invalid={!meta.dirtySinceLastSubmit && meta.submitError ? true : false}
			step={step}
			min={min}
			max={max}
			readOnly={readOnly}
			aria-describedby={name + '-invalid'}
			placeholder={props.placeholder ?? (showTranslationPlaceholder ? translatedFieldValue : '')}
			data-testid={testId || `field-${toKebabCase(name)}`}
			rows={rows}
			cols={cols}
		/>
	);

	if (type === 'hidden') {
		return inputComponent;
	}

	return (
		<FormGroup
			className={classnames('', {
				'form-group--dirty': indicateDirty && meta.dirty,
				[`${className}`]: className,
			})}
		>
			<FieldLabel {...props} meta={meta} htmlFor={name} />

			<div
				className={classnames('input-wrapper', {
					'input-wrapper__subtext-wrapper': subtext,
				})}
			>
				{prefix ? <div className="input-wrapper__prefix">{prefix}</div> : null}
				{inputComponent}
				{suffix ? <div className="input-wrapper__suffix">{suffix}</div> : null}
				{subtext ? <FieldSubtext text={subtext} /> : null}
				{renderIcon ? renderIcon() : null}
			</div>

			<ValidationMessages id={name + '-invalid'} meta={meta} />
		</FormGroup>
	);
}

export default Input;
